/*
 * File: Settings.jsx
 * Project: lets-talk-web
 *
 * Created by Sadra Williams on February 23, 2026 at 7:45 PM
 *
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useRef, useState
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// Services
import { beginChildPasswordReset } from '../../../services/password';


// Utilities
import { createStateLocale } from '../../../utilities/locale';
import { parseFirstLastName } from '../../../../utilities/utilities';
import { toastError, toastSuccess } from '../../../utilities/toaster';

// Components
import {
	Meta,
	AppNavigation,
	Typography,
	Button
} from '../../../components';

// Styles
import * as S from './Settings.styles';

// Constants
import { ROLES } from '../../../../Constants';


/**
 * Component
 */

const Settings = ({ meta, locale, data }) => {

	// Set state
	const [componentData, setComponentData] = useState(null);
	const [pageStatus, setPageStatus] = useState('idle');
	const [isLoading, setIsLoading] = useState(false);


	// Get current locale from hook
	const clientLocale = useSelector((state) => state.locale.value);
	const stateLocale = createStateLocale(clientLocale, locale);

	// Get current user from hook
	const user = useSelector((state) => state.user.value);

	// Get ui mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Create reference for components
	const isMounted = useRef(true);

	// Initialize component data function
	const fetchDataForPage = async () => {

		// Update page status
		setPageStatus('loading');

		// NV

		console.log(componentData);
		setComponentData({});
		setPageStatus('success');

	};

	// Handle log in function
	const handleChildForgotPassword = async () => {


		// Get parameters
		const { email } = user;

		// Validate parameters
		if (!email) {
			toastError(uiMode, 'Whoops. We can\'t find an account with those details. Give it another go.', { duration: 7000 });
			return;
		}

		// Set loading state
		setIsLoading(true);

		// Begin password reset process
		beginChildPasswordReset({ email }).then(() => {

			// Show success toast
			toastSuccess(uiMode, 'Success! Check your inbox for an email to reset your child\'s account password.', { duration: 7000 });

			// Set isLoading to false after 1 second
			setTimeout(() => {
				setIsLoading(false);
			}, 1000);

		}).catch(({ response }) => {

			setIsLoading(false);

			// Show error message
			if (response?.data?.message) {
				toastError(uiMode, 'Whoops. An error has occurred.', { duration: 7000 });
			} else {
				toastError(uiMode, 'Whoops. An error has occurred.', { duration: 7000 });
			}
		});
	};

	// Handle actions on app component state change
	useEffect(() => {

		// Ensure initial page loading is not complete
		if (pageStatus === 'idle') {

			// Fetch data state for page
			fetchDataForPage();
		}
	}, [pageStatus]);

	// Handle component initialization
	useEffect(() => {

		// Set state
		isMounted.current = true;

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} locale={stateLocale} />

			{/* Component Content */}
			<AppNavigation data={data}>
				<S.Wrapper left right>

					{/* Title Widget */}
					<S.TitleWidget>
						<Typography tag="h4" weight="medium">Settings</Typography>
					</S.TitleWidget>

					{/* Header */}
					<S.Header>
						<Typography style={{ height: 'auto' }} variation="2" tag="p" weight="medium">
							First Name:
							{' '}
							{parseFirstLastName(user.name)?.firstName}
						</Typography>
						{(user.role.primary === ROLES.PARENT || user.role.primary === ROLES.ADMIN) && (
							<Typography style={{ height: 'auto' }} variation="2" tag="p" weight="medium">
								Email:
								{' '}
								{user.email}
							</Typography>
						)}

						<br />
						{(user.role.primary === ROLES.PARENT || user.role.primary === ROLES.ADMIN) && (
							<Button onClick={handleChildForgotPassword}>
								<Typography tag="p" variation="2" weight="bold">
									{isLoading ? 'Sending...' : 'Reset child password'}
								</Typography>
							</Button>
						)}
					</S.Header>


				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

Settings.propTypes = {
	meta: PropTypes.shape(),
	locale: PropTypes.shape(),
	data: PropTypes.shape(),
};
Settings.defaultProps = {
	meta: {},
	locale: {},
	data: null
};


/**
 * Exports
 */

export default Settings;
